import React, { useEffect, useState } from 'react';
import { Search, PhoneOutgoing } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '—';
const formatDuration = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const formatEur = (n) => (n == null ? '—' : `${n.toFixed(2)} €`);

const STATUS_BADGE = {
  queued: 'badge-gray', initiated: 'badge-warning', ringing: 'badge-warning',
  answered: 'badge-success', completed: 'badge-success',
  busy: 'badge-danger', 'no-answer': 'badge-danger', failed: 'badge-danger',
};

const OutboundCalls = () => {
  const modal = useModal();
  const [calls, setCalls] = useState([]);
  const [payloads, setPayloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('outboundcalls:search', '');
  const [tab, setTab] = useUserPreference('outboundcalls:tab', 'calls');
  const [viewMode, setViewMode] = useViewMode('outboundcalls');
  const flCalls = useFieldLayout('outboundcalls-calls', viewMode);
  const flPayloads = useFieldLayout('outboundcalls-payloads', viewMode);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([
        api.get('/minerva-outbound?limit=200'),
        api.get('/minerva-outbound/payloads?limit=200'),
      ]);
      setCalls(Array.isArray(c.data) ? c.data : []);
      setPayloads(Array.isArray(p.data) ? p.data : []);
    } catch (e) {
      modal.error('Error cargando llamadas salientes');
    } finally {
      setLoading(false);
    }
  };

  const filteredCalls = calls.filter((c) => {
    const q = search.toLowerCase();
    return !q ||
      (c.toNumber || '').toLowerCase().includes(q) ||
      (c.fromNumber || '').toLowerCase().includes(q) ||
      (c.resolvedName || '').toLowerCase().includes(q) ||
      (c.callType || '').toLowerCase().includes(q) ||
      (c.status || '').toLowerCase().includes(q);
  });

  const filteredPayloads = payloads.filter((p) => {
    const q = search.toLowerCase();
    return !q ||
      (p.toNumber || '').toLowerCase().includes(q) ||
      (p.recipientName || '').toLowerCase().includes(q) ||
      (p.freeTextMessage || '').toLowerCase().includes(q);
  });

  const callColumns = [
    { key: 'contact', header: 'Destinatario', className: 'dt-strong', fixed: true, render: (c) => (
      <div>
        {c.resolvedName || c.toNumber}
        <div className="dt-sub" style={{ fontWeight: 400 }}>{formatDate(c.createdAt)}</div>
      </div>
    ) },
    { key: 'status', header: 'Estado', render: (c) => (
      <span className={`badge ${STATUS_BADGE[c.status] || 'badge-gray'}`}>{c.status}</span>
    ) },
    { key: 'toNumber', header: 'A', className: 'dt-nowrap', render: (c) => c.toNumber },
    { key: 'fromNumber', header: 'Desde', className: 'dt-nowrap', render: (c) => c.fromNumber },
    { key: 'duration', header: 'Duración', render: (c) => formatDuration(c.durationSec) },
    { key: 'cost', header: 'Coste', className: 'num', render: (c) => formatEur(c.costEur) },
    { key: 'callType', header: 'Tipo', render: (c) => c.callType },
    { key: 'lineType', header: 'Línea', render: (c) => c.lineType },
    { key: 'lastEventType', header: 'Último evento', render: (c) => c.lastEventType || '—' },
  ];

  const callFields = flCalls.fields(callColumns);

  const payloadColumns = [
    { key: 'recipient', header: 'Destinatario', className: 'dt-strong', fixed: true, render: (p) => (
      <div>
        {p.recipientName || p.toNumber || '—'}
        <div className="dt-sub" style={{ fontWeight: 400 }}>{formatDate(p.createdAt)}</div>
      </div>
    ) },
    { key: 'deliveryStatus', header: 'Estado', render: (p) => (
      <span className={`badge ${p.deliveryStatus === 'delivered' ? 'badge-success' : p.deliveryStatus === 'pending' ? 'badge-warning' : 'badge-gray'}`}>
        {p.deliveryStatus}
      </span>
    ) },
    { key: 'toNumber', header: 'Tel', className: 'dt-nowrap', render: (p) => p.toNumber || '—' },
    { key: 'orderedByName', header: 'Encargado por', render: (p) => p.orderedByName || '—' },
    { key: 'freeTextMessage', header: 'Mensaje', render: (p) => (
      p.freeTextMessage ? <span style={{ fontSize: 12, color: '#475569' }}>{p.freeTextMessage}</span> : '—'
    ) },
    { key: 'attempts', header: 'Intentos', render: (p) => `${p.deliveryAttempts}/${p.maxDeliveryAttempts}` },
  ];

  const payloadFields = flPayloads.fields(payloadColumns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Llamadas salientes</h1>
          <p className="page-subtitle">{calls.length} llamadas · {payloads.length} mensajes pendientes/entregados</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${tab === 'calls' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('calls')}>Llamadas ({calls.length})</button>
          <button className={`btn ${tab === 'payloads' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('payloads')}>Mensajes ({payloads.length})</button>
        </div>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input className="form-input" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={tab === 'calls' ? callFields : payloadFields}
              hidden={tab === 'calls' ? flCalls.hidden : flPayloads.hidden}
              onToggleField={tab === 'calls' ? flCalls.toggle : flPayloads.toggle}
              onReorderFields={tab === 'calls' ? flCalls.reorder : flPayloads.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : tab === 'calls' ? (
          filteredCalls.length === 0 ? (
            <div className="empty-state"><PhoneOutgoing size={64} /><h3>Sin llamadas salientes</h3></div>
          ) : viewMode === 'table' ? (
            <DataTable scope="outboundcalls-calls" columns={flCalls.orderColumns(callColumns)} rows={filteredCalls} hiddenKeys={flCalls.hidden} />
          ) : (
            <div className="items-grid">
              {filteredCalls.map((c) => (
                <div className="item-card" key={c.id}>
                  <div className="item-card-header">
                    <div className="item-card-avatar">→</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="item-card-title"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {c.resolvedName || c.toNumber}
                      </div>
                      <div className="item-card-subtitle">{formatDate(c.createdAt)}</div>
                    </div>
                    {!flCalls.isHidden('status') && <span className={`badge ${STATUS_BADGE[c.status] || 'badge-gray'}`}>{c.status}</span>}
                  </div>
                  <div className="item-card-body">
                    {!flCalls.isHidden('toNumber') && <div className="item-card-row" style={flCalls.cardStyle('toNumber')}><span className="item-label">A</span><span>{c.toNumber}</span></div>}
                    {!flCalls.isHidden('fromNumber') && <div className="item-card-row" style={flCalls.cardStyle('fromNumber')}><span className="item-label">Desde</span><span>{c.fromNumber}</span></div>}
                    {!flCalls.isHidden('duration') && <div className="item-card-row" style={flCalls.cardStyle('duration')}><span className="item-label">Duración</span><span>{formatDuration(c.durationSec)}</span></div>}
                    {!flCalls.isHidden('cost') && <div className="item-card-row" style={flCalls.cardStyle('cost')}><span className="item-label">Coste</span><span>{formatEur(c.costEur)}</span></div>}
                    {!flCalls.isHidden('callType') && <div className="item-card-row" style={flCalls.cardStyle('callType')}><span className="item-label">Tipo</span><span>{c.callType}</span></div>}
                    {!flCalls.isHidden('lineType') && <div className="item-card-row" style={flCalls.cardStyle('lineType')}><span className="item-label">Línea</span><span>{c.lineType}</span></div>}
                    {!flCalls.isHidden('lastEventType') && c.lastEventType && <div className="item-card-row" style={flCalls.cardStyle('lastEventType')}><span className="item-label">Último evento</span><span>{c.lastEventType}</span></div>}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          filteredPayloads.length === 0 ? (
            <div className="empty-state"><PhoneOutgoing size={64} /><h3>Sin mensajes</h3></div>
          ) : viewMode === 'table' ? (
            <DataTable scope="outboundcalls-payloads" columns={flPayloads.orderColumns(payloadColumns)} rows={filteredPayloads} hiddenKeys={flPayloads.hidden} />
          ) : (
            <div className="items-grid">
              {filteredPayloads.map((p) => (
                <div className="item-card" key={p.id}>
                  <div className="item-card-header">
                    <div className="item-card-avatar">✉</div>
                    <div style={{ flex: 1 }}>
                      <div className="item-card-title">{p.recipientName || p.toNumber || '—'}</div>
                      <div className="item-card-subtitle">{formatDate(p.createdAt)}</div>
                    </div>
                    {!flPayloads.isHidden('deliveryStatus') && (
                      <span className={`badge ${p.deliveryStatus === 'delivered' ? 'badge-success' : p.deliveryStatus === 'pending' ? 'badge-warning' : 'badge-gray'}`}>
                        {p.deliveryStatus}
                      </span>
                    )}
                  </div>
                  <div className="item-card-body">
                    {!flPayloads.isHidden('toNumber') && p.toNumber && <div className="item-card-row" style={flPayloads.cardStyle('toNumber')}><span className="item-label">Tel</span><span>{p.toNumber}</span></div>}
                    {!flPayloads.isHidden('orderedByName') && p.orderedByName && <div className="item-card-row" style={flPayloads.cardStyle('orderedByName')}><span className="item-label">Encargado por</span><span>{p.orderedByName}</span></div>}
                    {!flPayloads.isHidden('freeTextMessage') && p.freeTextMessage && (
                      <div className="item-card-row" style={{ ...flPayloads.cardStyle('freeTextMessage'), flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        <span className="item-label">Mensaje</span>
                        <span style={{ fontSize: 12, color: '#475569' }}>{p.freeTextMessage}</span>
                      </div>
                    )}
                    {!flPayloads.isHidden('attempts') && <div className="item-card-row" style={flPayloads.cardStyle('attempts')}><span className="item-label">Intentos</span><span>{p.deliveryAttempts}/{p.maxDeliveryAttempts}</span></div>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default OutboundCalls;
